<script setup lang="ts">
import { useGoogleAnalyticsPageView } from '~/composables/google-analytics'

const { $seoManager } = useNuxtApp()

const seo = $seoManager.generateSeoInfo({
  title: 'Terms',
})
useHead(seo)
useGoogleAnalyticsPageView(seo.title)
</script>

<template>
  <SimplePageContent>
    <SimplePageTitle title="Terms" />

    <div class="break-words [&>p]:my-5">
      <p>
        <strong>
          BENCHMARK MINERAL INTELLIGENCE LIMITED TERMS AND CONDITIONS
        </strong>
      </p>
      <p><strong>IMPORTANT – PLEASE READ CAREFULLY</strong></p>
      <p><strong>1 INTRODUCTION</strong></p>
      <p>
        1.1 These are the terms and conditions of service (”
        <strong>Terms</strong> “) which govern the use of all websites
        (“<strong>Sites</strong>“) and related Services (as defined below)
        owned, operated or provided by Benchmark Mineral Intelligence Limited
        (“<strong>Benchmark</strong>“). Your use of the Sites and Services is
        conditional on you complying with these Terms. Any additional terms and
        conditions that are posted on specific Sites or agreed in relation to
        any Service (“<strong>Specific Terms</strong>”) shall be deemed
        incorporated into these Terms, and to the extent that any of these Terms
        conflict with any such Specific Terms, the Specific Terms shall prevail.
      </p>
      <p>
        1.2 Please note:
        <strong
          >where you visit, register and/or subscribe to a Benchmark Site or
          related Service, these Terms will be governed by the laws of England
          and Wales and will operate as between Benchmark and you, to the
          maximum extent permissible under the law of the territory that you are
          located in.</strong
        >
      </p>
      <p>
        1.3 Provision of registration information by you to us (where relevant)
        will constitute an offer by you to become a Registered User or
        Subscriber (as applicable) on these Terms and any other terms applicable
        to the specific registration or subscription (including, without
        limitation, terms relating to the number of licensed users, the
        applicable charges and period of any subscription). You will not become
        a Registered User or Subscriber (as applicable) nor be entitled to
        access or receive Sites, Content or Services relating to the relevant
        registration or subscription unless and until we notify you of our
        acceptance of such offer, by providing you with a user name and password
        or otherwise (in accordance with the registration or subscription
        process on that Site).
      </p>
      <p>
        1.4 Please read these Terms carefully. Each time you access a Site or
        use a Service, these Terms will apply. These Terms may be amended in
        accordance with Clause 17 below. It is your responsibility to bring
        these Terms to the attention of anyone who may, through you, access a
        Site or view or use any Content (as defined below).If you have any
        questions regarding these terms with any of the Benchmark Sites, please
        contact us at
        <a href="mailto:amiller@benchmarkminerals.com">
          amiller@benchmarkminerals.com</a
        >. If you do not wish to be bound by these Terms, do not use any of the
        Sites or Services.
      </p>
      <p>
        1.5 You may access a Site as follows: (a) if you go through a
        registration process on that Site (if available), as a Registered User
        (in accordance with Clause 6 below); or (b) if you register as a
        Subscriber to our subscription Services which we offer or may offer in
        the future (in accordance with Clauses 6 and 7 below); or (c) by
        accessing the Site without registration or subscription under (a) or (b)
        (as a Visitor). In such circumstances, your use of the Site or any
        Content (as defined below) constitutes your acceptance of these Terms.
      </p>
      <p>
        1.6 To the extent any individual accesses a Site or uses a Service as an
        Agent of any legal entity which is a Subscriber, the Agent warrants that
        he or she has authority to agree to these Terms on behalf of the
        relevant legal entity.
      </p>
      <p>
        1.7 Some areas on a Site may only be available to Registered Users or
        Subscribers (see Clause 6 below).
      </p>
      <p>
        1.8 A Site may be supported by Sponsors whose names appear on the Site
        and whose own websites may be accessed by Internet links from that Site
        (see Clause 13 below).
      </p>
      <p>
        1.9 Please also see our privacy statement in relation to use of your
        personal data by Benchmark.
      </p>
      <p><strong>2 DEFINITIONS</strong></p>
      <p>
        2.1 “<strong>you/your</strong>” means (as appropriate) the Visitor,
        Registered User and/or Subscriber accessing a Site or using a Service.
      </p>
      <p>2.2 “<strong>we/us/our</strong>” means Benchmark.</p>
      <p>
        2.3 “<strong>Agent</strong>” means an employee, partner, director,
        contractor, agent or representative of any party.
      </p>
      <p>
        2.4 “<strong>Connected Networks</strong>” means certain third party
        social networking sites (including without limitation Twitter and
        LinkedIn) which we may choose to affiliate with a certain Site.
      </p>
      <p>
        2.5 “<strong>Content</strong>” means all material, data, articles,
        information and products on the Sites or provided as part of a Service
        (including, without limitation, any Electronic Books or publications
        sent by post as part of a Service, but excluding any Posted Material).
      </p>
      <p>
        2.6 “<strong>Deep Linking</strong>” means the potential linking to an
        internal or subsidiary page of the Site located one or several levels
        down from the home page.
      </p>
      <p>
        2.7 “<strong>Electronic Books</strong>” means any electronic book
        including without limitation ebooks and yearbooks or other written
        material available for download from any of our Sites, e-mailed directly
        to you as part of a Service or disseminated by other electronic means
        for viewing without accessing a Site.
      </p>
      <p>
        2.8 “<strong>External Sites</strong>” means websites other than the
        Sites provided by Sponsors or other third parties.
      </p>
      <p>
        2.9 “<strong>Framing</strong>” means the bringing up of or presenting
        Content within another website.
      </p>
      <p>
        2.10 “<strong>Intellectual Property</strong>” means copyright, trade
        marks, database rights and/or any other intellectual property rights as
        recognised in any jurisdiction.
      </p>
      <p>
        2.11 “<strong>Interactive Areas</strong>” means bulletin boards, email
        services, discussion groups, messaging systems, including instant
        messaging facilities, and other public areas that allow interactivity
        between users of our Sites and/or feedback to be provided to us.
      </p>
      <p>
        2.12 “<strong>Investment</strong>” means any shares, security, financial
        product or other investment.
      </p>
      <p>
        2.13 “<strong>Minimum Term</strong>” means the initial period of one
        year from the commencement of any subscription (or such other period as
        may be agreed in writing for any specific subscription).
      </p>
      <p>
        2.14 “<strong>Posted Material</strong>” means the information and/or
        materials posted to Interactive Areas by you or any third party.
      </p>
      <p>
        2.15 “<strong>Registered User</strong>” means a user of certain Sites
        and/or Services who has registered with us in order to access such Sites
        and/or Services in under Clause 6.1(a).
      </p>
      <p>
        2.16 “<strong>Representative</strong>” means our suppliers, officers,
        employees, partners, affiliates, subsidiaries, successors and assigns,
        agents or representatives.
      </p>
      <p>
        2.17 “<strong>Services</strong>” means (a) the supply of on-line or
        electronic information, publications and data products (including,
        without limitation, the delivery of the same over the internet, or via
        mobile phone apps or other digital devices); (b) the supply of hard-copy
        publications and other materials; and (c) other services relating to a
        Site (including, without limitation, any Interactive Areas).
      </p>
      <p>
        2.18 “<strong>Social Media Page</strong>” means a page created, operated
        or otherwise provided by Benchmark via a third party social media site,
        network, application and/or service.
      </p>
      <p>
        2.19 “<strong>Software</strong>” means the Intellectual Property in any
        software that is made available for download from our Sites.
      </p>
      <p>
        2.20 “<strong>Sponsors</strong>” means sponsoring organisations and
        advertisers.
      </p>
      <p>
        2.21 “<strong>Subscriber</strong>” means a user of our Sites and/or
        Services who has registered for our subscription Services under Clause
        6.1(b).
      </p>
      <p>
        2.22 “<strong>Third Party Services</strong>” products or services
        provided by anyone.
      </p>
      <p>
        2.23 “<strong>Visitor</strong>” means a user who accesses our Sites
        without registration or subscription.
      </p>
      <p>
        <strong>
          3 YOUR USE OF CONTENT, TRADE MARKS AND ANY OTHER INTELLECTUAL PROPERTY
        </strong>
      </p>
      <p>
        3.1 All rights in and to the Content and the Sites belong to us, or our
        third party content providers and are protected by the Intellectual
        Property laws of the UK, US and other countries. We may license third
        parties to use the Content at our sole discretion.
      </p>
      <p>
        3.2 You may use the Sites and the Content solely for your own personal
        use and benefit and not for resale or other transfer or disposition to
        any other person or entity. You may not use, reproduce, modify,
        transfer, exploit, distribute or dispose of any Content for any
        commercial purposes, whether direct or indirect, in any manner that
        might compete with our business (including, without limitation, by
        making Content available to anyone other than a Subscriber where such
        Content is accessible through subscription only).
      </p>
      <p>
        3.3 Subject to the limitations in Clause 3.2 above and your compliance
        with Clause 4, and further subject to Clause 11 in relation to
        Electronic Books, you may: (a) retrieve and display Content on a
        computer screen or other digital device, print a single copy of
        individual pages on paper (but not photocopy them) and store such pages
        in electronic form on disc or other digital media (but not on any server
        or other storage device connected to a network); and (b) use the “Email
        this story to a friend” option where the facility is available to email
        an article from a Site to other individuals without further charge,
        provided such other individuals also comply with the restrictions on use
        in Clause 3.2 above.
      </p>
      <p>
        3.4 Notwithstanding Clause 3.2, you may quote from or paraphrase
        extracts of the Content to the extent permitted and strictly in
        accordance with any applicable Specific Terms, or as otherwise permitted
        by law, on the condition that appropriate source and copyright
        attribution is given in each case.
      </p>
      <p>
        3.5 Save for where provided otherwise in these Terms, distributing,
        sharing, copying, posting, scanning, forwarding, selling, publishing,
        retransmitting or otherwise providing access to any part of the Content
        to anyone without our express prior written permission, is not
        authorised and may be a violation of national copyright laws, including
        without limitation 17 USC 101 et seq, and the UK Copyright, Designs and
        Patents Act 1988.
      </p>
      <p>
        3.6 Any request for permission to republish, reprint or use any articles
        from any of the Sites or our trade marks for any purpose other than
        those permitted under this Clause 3 (or any relevant Specific Terms)
        should be sent to
        <a href="mailto:amiller@benchmarkminerals.com"
          >amiller@benchmarkminerals.com</a
        >.
      </p>
      <p>
        3.7 For the avoidance of doubt, where the provisions of any applicable
        law prohibit the extent to which certain activities can be restricted
        under this Clause 3, any such restrictions shall apply to the fullest
        extent permitted in accordance with such law.
      </p>
      <p><strong>4 YOUR OBLIGATIONS</strong></p>
      <p>
        4.1 The Sites are not intended for users of under eighteen (18) years of
        age, and such individuals are not permitted to be Registered Users or
        Subscribers. If individuals under eighteen (18) years of age wish to
        view material on any Site as a Visitor, their parent or guardian must
        agree to these Terms on their behalf.
      </p>
      <p>
        4.2 You represent and warrant that: (a) you have the capacity to agree
        to these Terms; and (b) you are at least eighteen (18) years of age.
      </p>
      <p>
        4.3 You undertake that you will, and if you are a Subscriber, you will
        procure that any licensed user under your subscription will: (a) comply
        with all applicable laws in relation to the Sites and Services,
        including without limitation, laws relating to the use of Intellectual
        Property; (b) not use the Sites or the Content outside the scope of
        permitted use under Clause 3 above, nor infringe any Intellectual
        Property or other rights in or relating to the Site or the Services or
        of any third party; (c) not copy, reproduce, recompile, decompile,
        disassemble, reverse engineer, upload, download, transmit, create
        derivative works from or otherwise exploit or tamper with any Site or,
        subject to Clause 14, any software embodied in any Site or Service; (d)
        not prevent or restrict the use of any Site or Service by other
        authorised users nor hack into or cause damage to any server or other
        equipment operated by us; and (e) comply fully with these Terms
        (including, without limitation, the obligations set out in Clause 10,
        and any additional copyright notices and applicable Specific Terms).
      </p>
      <p>
        4.4 You are responsible for procuring the necessary equipment and the
        payment of telephone, internet and other charges necessary to access
        and/or use any Site or Service. We are not responsible for the
        reliability or continued availability of the telephone lines, internet
        and equipment you use to access and/or use a Site or a Service.
      </p>
      <p>
        4.5 Any Content sent for overseas delivery (including, without
        limitation, any Content for delivery outside the European Union) may be
        subject to import duties and taxes collected at the final destination.
        All sales or use taxes, import duties or additional customs charges must
        be borne entirely by you. We have no control over these charges and we
        expressly disclaim any responsibility or liability for them. Customs
        policies vary widely from country to country, so you should contact your
        local customs office for further information.
      </p>
      <p>
        4.6 Additionally, when ordering from us, you are considered the importer
        of record and must comply with all law and regulations of the country in
        which you are receiving the goods. Our international customers should be
        aware that cross-border deliveries are subject to opening and inspection
        by customs authorities. We may provide certain order, delivery and
        product information (such as the product’s title) to our international
        carriers and such information may be communicated by the carriers to
        customs authorities in order to facilitate customs clearance and comply
        with local laws. If the order is a gift, the package will be marked
        ‘Gift’ but the cost or value of the item may still be stated on the
        customs form and/or the package.
      </p>
      <p><strong>5 OUR OBLIGATIONS</strong></p>
      <p>
        5.1 Subject to and without limitation to Clause 15, we will exercise
        reasonable skill and care in our provision of the Services provided to
        Registered Users and Subscribers, but from time to time faults may
        occur, in which case we will use reasonable efforts to repair them as
        soon as reasonably practicable.
      </p>
      <p>
        5.2 We will endeavour to ensure all subscriptions or other Services are
        priced correctly on the Sites (or otherwise in the Content). However,
        errors may occur. If such a pricing error is discovered, we reserve the
        right to give you the option of either cancelling your order for any
        particular subscription or other Service or reconfirming your order at
        the correct price.
      </p>
      <p><strong>6 REGISTRATION AND SUBSCRIPTIONS</strong></p>
      <p>
        6.1 In order to access certain Sites (or specific Content within certain
        Sites), and/or to receive certain Services, you must register with us as
        either a Registered User or a Subscriber, as follows: (a) certain Sites,
        Content and/or Services are available free of charge once you register
        with us, in which case you may access or receive them on these Terms as
        a Registered User; or (b) charges are payable for receipt of certain
        Services (which may include access to certain Sites and Content), in
        which case you may receive such Services on these Terms as a Subscriber
        who has paid the relevant charges (in accordance with Clause 7 below)
        for a specified period of time, and either a specified number of
        licensed users or a site licence.
      </p>
      <p>
        6.2 In order to register with us, you must provide us with accurate and
        complete information. It is your responsibility to inform us of any
        changes to that information (including, without limitation, your email
        address), by updating your details on the relevant section of the
        applicable Site or contacting us using contact details on the applicable
        Site. If you do not provide accurate and complete information when you
        register, we may not be able to provide the Services or Content that you
        request or that are suitable to you.
      </p>
      <p>
        6.3 In some cases, before you can make use of certain Services
        associated with a particular Site as a Registered User, you may need to
        create an account connected through Connected Networks. You understand
        that the creation of a Registered User account through the Connected
        Networks will require a connection to be created between the Site and
        the Connected Networks to facilitate your participation and activities
        on the Site.
      </p>
      <p>
        6.4 When you register, you will be given one or more user names and
        passwords, as appropriate for your registration or subscription to the
        relevant Site or Service.
      </p>
      <p>
        6.5 Except to the extent a user name and password is intended for more
        than one licensed user as agreed by us in writing, the following are not
        permitted: (a) any Registered User, Subscriber or licensed user under
        any subscription sharing their user name and password with any other
        person or entity, including with any Agent of such user; nor (b) access
        through a single user name and password being made available to multiple
        users on a network.
      </p>
      <p>
        6.6 To the extent that the terms of a multiple-user licence restricts
        licensed users to a particular category of individuals (e.g. employees
        of your organisation), you must notify us immediately, using the
        customer service contact details applicable to your subscription (as
        detailed at the end of these Terms), if a licensed user ceases to fall
        within the category specified.
      </p>
      <p>
        6.7 Notwithstanding the restrictions in Clause 6.5 above, you are
        responsible for all access and use of any Site or Service by you or
        anyone else using any of your user names and passwords and for
        preventing unauthorised use of any of your user names and passwords. If
        you believe there has been any breach of security (such as the
        disclosure, theft or unauthorised use of any user name, password or any
        payment information), you must notify us immediately by emailing
        <a href="mailto:amiller@benchmarkminerals.com"
          >amiller@benchmarkminerals.com</a
        >.
      </p>
      <p>
        6.8
        <strong
          >We reserve the right to access and monitor password-protected
          information, including any information which you upload or input and
          any information concerning your activities in relation to a Service or
          a Site or any Content, in order to fulfil our obligations in these
          Terms, to protect or comply with our legal rights and obligations and
          for other purposes as set out in our privacy statement and all in
          accordance with the terms of our privacy statement. We may also use
          cookies and similar technical tools as set out in our privacy
          statement to collect information about your visit to our Sites and
          display advertisements that may be of interest to you and for such
          other purposes as are set out in our privacy statement</strong
        >
        . <strong>Our</strong> <strong>Privacy Policy</strong>
        <strong
          >sets out further details of our use of cookies and similar technical
          tools together with details of how to turn those cookies on and
          off.</strong
        >
      </p>
      <p>
        6.9 As a Registered User and/or Subscriber, you agree we may refer to
        the name of your business and to the Services we have provided to you
        when marketing our business.
      </p>
      <p><strong>7 SUBSCRIPTION CHARGES AND REFUND POLICY</strong></p>
      <p>
        7.1 Subscribers must pay certain charges in order to receive the
        relevant subscription Service (unless we agree otherwise for a trial
        period). In most cases, details of applicable charges can be found on
        the specific Sites relating to the relevant subscription Service. Where
        charges are not published on a Site, the applicable charges will be as
        agreed between you and us. All payments (including any applicable taxes)
        must be made in any currency that we may specify. We may direct you to
        make payments to a third party payment agent. If we (or our designated
        agent) do not receive payment for any charges due, we may immediately
        suspend your rights in relation to the relevant Service (including any
        relevant Site and/or Content). You are responsible for the payment of
        all charges associated with the use of the Service, Site and/or Content
        using your user name and password.
      </p>
      <p>
        7.2 If we cancel your rights in relation to any subscription Service in
        accordance with Clause 8.5(b) below, unless you are or have been in
        breach of these Terms, we shall refund you on a pro rata basis any
        subscription charges you have paid in advance (for the relevant
        cancelled subscription) which relate to any unexpired part of the
        subscription period at the date of cancellation. You will still be
        responsible for any fees or other charges incurred by you until the
        cancellation of your rights in relation to the relevant subscription
        Service.
      </p>
      <p>
        7.3 If you cancel your subscription to any Service in accordance with
        Clause 8.4 below prior to the end of the subscription period: (a)
        subject to clauses 7.3(b) and 7.3(c) below, you shall be entitled to a
        refund on a pro rata basis of any subscription charges you have paid in
        advance for any complete months remaining of the subscription period at
        the date of cancellation beyond the Minimum Term; (b) we shall deduct
        from the refund due an administration fee of £100, US$150 or €150
        (depending on the relevant currency of payment); and (c) if you were
        granted any discount on the full value of the subscription charges for
        the subscription period, we may recalculate the value of the Minimum
        Term of the subscription period on the basis that no discount was
        applied to such Minimum Term.
      </p>
      <p>
        7.4 Without prejudice to Clause 1.3 above, the parts of the Sites
        showing Services or other products and services for purchase are
        intended as advertisements only and shall not constitute offers to sell
        those Services, products or services by us, our Sponsors or any of our
        divisions. All advertised prices for such products or services are
        subject to change without notice. See also Clause 13 below in relation
        to third party products and services.
      </p>
      <p><strong>8 TERM AND CANCELLATION</strong></p>
      <p>
        8.1 With the exception of subscription Services where a Subscriber loses
        access on expiration of their subscription, use of and access to the
        Sites and Services is not subject to any particular time limits.
      </p>
      <p>
        8.2 As a Visitor, you may cancel your access to and use of any Site (or
        receipt of any related Service) on these Terms at any time by
        discontinuing to access and use that Site and associated Content.
      </p>
      <p>
        8.3 As a Registered User, you may cancel your registration at any time
        by electronic notice to us using the contact information on the relevant
        Site or otherwise notified to you.
      </p>
      <p>
        8.4 You may cancel your subscription on no less than thirty (30) days’
        notice to us by post or email to take effect at any time after the end
        of the Minimum Term using the customer service contact details
        applicable to your subscription as detailed at the end of these Terms.
        You should quote the subscription reference number. There may be a
        period after the date of cancellation during which you continue to
        receive publications sent to you as part of your subscription. However,
        you will not be charged for such publications. For the avoidance of
        doubt, you shall not be entitled to cancel your subscription prior to
        the end of the Minimum Term.
      </p>
      <p>
        8.5 We may cancel your (and, if you are a Subscriber, your licensed
        users’) access to and use of any Site and Content and/or your receipt of
        any Service on these Terms (including, without limitation, any
        registration or subscription) at any time: (a) if you are a Registered
        User, by email notice to you; or (b) if you are a Subscriber, by email
        notice to you; or (c) if you are a Visitor or if you (or, if you are a
        Subscriber, any of your licensed users) commit a breach of any provision
        of these Terms, immediately without us having to provide notice to you.
      </p>
      <p>
        8.6 To the extent that the Specific Terms for any subscription entitle
        you to download and store certain Content, upon cancellation of such
        subscription by either you or us, you agree to delete all such Content
        stored in your possession or under your control within 30 days of
        cancellation, unless otherwise agreed with us in writing, or as required
        by applicable law. For the avoidance of doubt, where applicable law does
        require the retention of certain Content beyond the 30 day period, you
        agree that it shall only be retained to the extent required under such
        law and shall not be used for commercial purposes.
      </p>
      <p>
        8.7 Cancellation of your access to and use of any Site and Content
        and/or your receipt of any Service shall not affect any provision of
        this agreement which is expressly or by implication intended to come
        into effect or to continue in effect after such cancellation or expiry,
        including but not limited to Clauses 3.1, 3.5, 3.6, 7.2, 7.3, 8.7, 8.8,
        10.2, 10.3, 10.7, 13.3, 15, 16 and 22.
      </p>
      <p>
        8.8 Cancellation of your access to and use of any Site and Content
        and/or your receipt of any Service on these Terms (including, without
        limitation, any registration or subscription), shall not affect our
        right to receive any outstanding sums due to us at the date of
        cancellation, which sums shall be payable within fifteen (15) days of
        such cancellation.
      </p>
      <p><strong>9 CONTENT BY EMAIL</strong></p>
      <p>
        9.1 We may give you the option to request Content by email. We or our
        Third Party Service providers will also send you emails to keep you
        updated on developments in the Sites and Services. Your registration for
        and/or subscription to such an email Service and your use of the Content
        received through these email Services will be subject to these Terms.
        Subject to Clause 9.2, you can unsubscribe to our email Service by
        changing your email preferences on the relevant Site.
      </p>
      <p>
        9.2 For the avoidance of doubt, although you may opt out of receiving
        promotional messages under Clause 9.1, we retain the right to send you
        informational email messages about your account or administrative
        notices regarding the Site, as permitted under the CAN-SPAM Act (15
        U.S.C. §7701 et seq.) and other applicable laws.
      </p>
      <p><strong>10 INTERACTIVE SERVICES</strong></p>
      <p>
        10.1 A Site and/or a Social Media Page may contain Interactive Areas
        including Posted Material. We do not control and are not responsible for
        Posted Material and as a result, cannot guarantee the veracity or
        accuracy of any Posted Material. All use of the Interactive Areas is at
        your risk and you should not rely on Posted Material in any way.
      </p>
      <p>
        10.2 You hereby grant us, and, if you are a Subscriber, agree to procure
        the grant to us by all licensed users under your subscription of, a
        non-exclusive, perpetual, royalty-free licence to use, reproduce, modify
        and/or sub-license all or part of your Posted Material, including to
        sub-license Group Companies or third parties to use the same. We may
        without notice to you or any third party delete, move or edit any Posted
        Material or part of it.
      </p>
      <p>
        10.3 To the extent permitted under applicable law, you hereby waive,
        and, if you are a Subscriber, agree to procure the waiver by all
        licensed users under your subscription of, all moral rights or rights of
        a similar nature in any jurisdiction in any Posted Material.
      </p>
      <p>
        10.4 You are responsible for the content of your Posted Material and you
        may not within the Interactive Areas or otherwise in relation to a
        Service or a Site (including, without limitation, any email Service),
        post, publish, link to, upload, download, send, distribute, use or
        re-use any information or material: (a) which is or could be taken to be
        the provision of advice (including, without limitation, investment
        advice) or a recommendation to buy or refrain from buying a particular
        Investment or which has the purpose of affecting the price or value of
        any Investment; (b) obtained in breach of confidence or which contains
        confidential information or infringes any Intellectual Property rights
        or rights of privacy or other rights of any third party; (c) which is
        offensive, threatening, abusive, indecent, defamatory, obscene,
        degrading or menacing, or is otherwise contrary to applicable law or
        regulation or promoting an illegal act; (d) which constitutes
        unsolicited advertising or promotional material, including but not
        limited to any chain emails, unsolicited commercial emails, unsolicited
        bulk email, “spam” or mail bombs; or (e) which constitutes or contains a
        virus or other harmful component or malware.
      </p>
      <p>
        10.5 Without prejudice to Clause 10.4 above, you may not: (a) use any
        Interactive Area to carry out criminal, fraudulent or illegal
        activities; (b) use any Interactive Area to impersonate any person,
        company, group or entity or misrepresent a relationship to or with any
        of the same; (c) disguise the origin of any message posted in any
        Interactive Area; nor (d) collect, store, disclose or otherwise process
        any personal data in relation to your use of any Interactive Area
        without the knowledge of the relevant individual or in breach of
        applicable data protection laws.
      </p>
      <p>
        10.6 You must comply with any additional rules which may, from time to
        time, be issued by us at particular sections of the Interactive Areas,
        and such rules shall be deemed incorporated into these Terms.
      </p>
      <p>
        10.7 By accessing the Interactive Areas, you agree to indemnify and hold
        us harmless against all claims, damages, costs and expenses (including
        legal and other professional fees) which we may incur as a result of any
        of your Posted Material.
      </p>
      <p>
        10.8 We reserve the right to remove Posted Material at any time, for any
        reason, and without notice, including without limitation for any failure
        to comply with the provisions of this Clause 10. If any Posted Material
        fails to comply with the provisions of this Clause 10, we reserve the
        right to prevent you from accessing the Interactive Areas.
        Notwithstanding the foregoing, we are under no obligation to review,
        monitor, delete or edit Posted Material on a regular basis.
      </p>
      <p>
        10.9 Our Representatives may also submit Posted Material to the
        Interactive Areas. In doing so, Representatives shall act in a personal
        capacity and any views they might express shall not be considered our
        views nor the views of a financial or other professional advisor.
      </p>
      <p>
        10.10 Where Clauses 10.1 to 10.9 apply to a Social Media Page, such
        Clauses shall be subject to the terms and conditions of use of the third
        party site, application and/or service on which such Social Media Page
        appears.
      </p>
      <p>
        10.11 Notice for Claims of Copyright Violations If you believe that your
        work has been copied and posted in the US on any of our Sites in a way
        that constitutes copyright infringement under US copyright law, you
        should provide our Managing Director(details below) with a written
        notice that sets forth the infringement details. To be effective, the
        notice must contain the following information: (a) a description of the
        copyrighted work that you believe has been infringed; (b) a description
        of the material that you claim is infringing the copyrighted work and a
        detailed description of where it is located on our web site; (c) your
        address, telephone number, and email address; (d) a written statement by
        you that you have a good faith belief that the disputed use is not
        authorised by the copyright owner, its agent, or the law; (e) a
        statement by you, made under penalty of perjury, that the above
        information in your notice is accurate and that you are the copyright
        owner or authorised to act on the copyright owner’s behalf; and (f) an
        electronic or physical signature of the person authorised to act on
        behalf of the owner of the copyright interest. Please send the written
        communication to our Managing Director at the following address: By mail
        or e-mail: Benchmark Mineral Intelligence Limited Simon Moores 3
        Coldbath Square London EC1R 5HL United Kingdom +44 20 3287 3399
        <a href="mailto:smoores@benchmarkminerals.com"
          >smoores@benchmarkminerals.com</a
        >
        If you are accessing our Sites as a Subscriber or Registered User, we
        reserve the right, in appropriate circumstances and at our discretion,
        to cancel your right of access to the Sites if you repeatedly infringe
        the Intellectual Property. For the avoidance of doubt, the process set
        out in this Clause 10.11 should only be used to report a claim of
        copyright infringement. Messages related to other matters will not
        receive a response through this process.
      </p>
      <p><strong>11 ELECTRONIC BOOKS</strong></p>
      <p>
        11.1 Subject to Clause 3.7, in the event that you purchase an Electronic
        Book from one of the Sites, you are granted a non-exclusive right to:
        (a) download the Electronic Book for storage and display in
        machine-readable form on no more than three devices; and (b) obtain a
        single printout of the Electronic Book.
      </p>
      <p>
        11.2 Electronic Books are non-refundable, save for where the wrong
        product was provided to you due to our error.
      </p>
      <p><strong>12 LINKING</strong></p>
      <p>
        12.1 You may create a link from your website to the home page of certain
        Sites (where they indicate that you may do so) provided that you
        complete the linking registration form available at the applicable Site
        (or by contacting us), and provided that you do not link to the Site
        from any website containing any material which would be in breach of
        Clauses 4.3(b) to 4.3(e) above or which is otherwise unlawful.
      </p>
      <p>
        12.2 Upon linking to any Site pursuant to these Terms, we shall grant
        you a non-exclusive, non-transferable, royalty-free license to use our
        trade marks solely for the purposes of providing an underlined, textual
        link from your website to the Site. No other use of our trade marks or
        name is permitted without our express prior written permission.
      </p>
      <p>
        12.3 Deep Linking or Framing is not permitted without our express prior
        written permission. You must seek and obtain our express prior written
        permission before Deep Linking or Framing the Site or any Content by
        contacting us and providing us with: (a) your name, email address and
        telephone number; (b) the name of your company; (c) the web address(es)
        where the proposed Deep Linking or Framing will occur; and (d) specific
        details about the contemplated Deep Linking or Framing.
      </p>
      <p>
        <strong>13 ADVERTISEMENTS, LINKS AND THIRD PARTY TRANSACTIONS</strong>
      </p>
      <p>
        13.1 A Site or a Service may contain: (a) links to External Sites. We
        shall endeavour to highlight such links although some External Sites may
        be co-branded with ours. External Sites may advertise or provide the
        opportunity for you to purchase Third Party Services; and/or (b)
        advertisements for, and/or the opportunity for you to purchase Third
        Party Services or products or services from us or our Group Companies.
      </p>
      <p>
        13.2 The content of External Sites and Third Party Services is not
        maintained or controlled by us and we are therefore not responsible for
        the availability, content or accuracy of External Sites or Third Party
        Services. We do not (a) make any warranty, express or implied, with
        respect to the use of the links provided on, or to, a Site or Service;
        (b) guarantee the accuracy, completeness, usefulness or adequacy of
        External Sites or Third Party Services; or (c) make any endorsement,
        express or implied, of any External Sites or Third Party Services. Any
        advertisement or promotion on a Site of a Third Party Service is not an
        inducement by us for you to enter into any agreement with the relevant
        third party.
      </p>
      <p>
        13.3 Any communications or contracts relating to Third Party Services,
        payment for and delivery of Third Party Services, and any other
        associated terms, conditions, warranties or representations are
        exclusively between you and the relevant third parties. You agree not to
        hold us liable for any loss or damage incurred as the result of any such
        contracts, communications or other dealings or the presence of such
        third parties on any Site.
      </p>
      <p><strong>14 SOFTWARE</strong></p>
      <p>
        The Software belongs to us or our suppliers. Your use of the Software is
        governed by the terms of any licence agreement that may accompany or be
        included with the Software. Do not install or use any Software unless
        you agree to such licence agreement.
      </p>
      <p><strong>15 LIMITATIONS OF LIABILITY</strong></p>
      <p>
        15.1 Nothing in these Terms shall exclude or limit our liability for
        fraud or intentional unlawful conduct by us, or death or personal injury
        resulting from our negligence and the remaining provisions of this
        Clause 15, shall be subject to this provision.
      </p>
      <p>
        15.2 <strong>DISCLAIMER:</strong> WITHOUT PREJUDUCE TO THE REMAINING
        PROVISIONS OF THIS CLAUSE 15, YOU AGREE THAT YOUR USE OF ANY CONTENT IS
        AT YOUR SOLE RISK AND ACKNOWLEDGE THAT ALL SITES, CONTENT AND SERVICES
        ARE PROVIDED “AS IS” AND “AS AVAILABLE”. THE CONTENT IS MADE AVAILABLE
        FOR YOUR GENERAL INFORMATION AND ANY ADVICE, OPINION, STATEMENT OR OTHER
        INFORMATION FORMING PART OF THE CONTENT IS NOT INTENDED FOR TRADING OR
        TO ADDRESS YOUR PARTICULAR REQUIREMENTS. THE CONTENT DOES NOT CONSTITUTE
        ANY FORM OF ADVICE, RECOMMENDATION OR ARRANGEMENT BY US (INCLUDING,
        WITHOUT LIMITATION, INVESTMENT ADVICE OR AN OFFER OR SOLICITATION TO BUY
        OR SELL ANY SECURITY, FINANCIAL PRODUCT OR OTHER INVESTMENT) AND IS NOT
        INTENDED TO BE RELIED UPON BY USERS IN MAKING (OR REFRAINING FROM
        MAKING) ANY SPECIFIC INVESTMENT OR OTHER DECISIONS. APPROPRIATE
        INDEPENDENT ADVICE SHOULD BE OBTAINED BEFORE MAKING ANY SUCH DECISION.
        THE CONTENT MAY INCLUDE INACCURACIES OR TYPOGRAPHICAL ERRORS. CHANGES
        ARE PERIODICALLY MADE TO THE CONTENT. SAVE AS PROVIDED IN THESE TERMS,
        NEITHER WE NOR OUR LICENSORS AND/OR REPRESENTATIVES (AS DEFINED IN
        CLAUSE 2.16 ABOVE) MAKE ANY REPRESENTATIONS OR WARRANTIES AND, TO THE
        FULLEST EXTENT ALLOWED BY LAW, EXCLUDE ALL IMPLIED WARRANTIES
        (INCLUDING, BUT NOT LIMITED TO, WARRANTIES OF SATISFACTORY QUALITY,
        TITLE AND FITNESS FOR A PARTICULAR PURPOSE) REGARDING (A) THE
        SUITABILITY OF THE INFORMATION; (B) THE ACCURACY, AVAILABILITY,
        RELIABILITY, COMPLETENESS OR TIMELINESS OF THE SITES, CONTENT OR
        SERVICES; OR (B) THE RESULTS OBTAINED FROM ACCESSING AND USING ANY SITE,
        SERVICE OR CONTENT. FURTHER, WE DO NOT REPRESENT OR WARRANT THAT (A) THE
        FUNCTIONS CONTAINED IN THE SITES OR SERVICES WILL BE UNINTERRUPTED OR
        ERROR-FREE; (B) DEFECTS WILL BE CORRECTED; (B) THE SITES, THE SERVICES
        OR THE SERVERS THAT MAKE THEM AVAILABLE ARE FREE OF VIRUSES OR OTHER
        HARMFUL COMPONENTS; NOR (D) THE USE OF ANY OF THE CONTENT WILL NOT
        INFRINGE THE INTELLECTUAL PROPERTY RIGHTS OF ANY THIRD PARTY.
      </p>
      <p>
        15.3 Neither we, nor any Representatives will be liable to you for: (a)
        any incidental, punitive, indirect, special or consequential damage,
        loss or expenses, including but not limited to any loss of business,
        contracts, revenue, or profits, any business interruption, security
        breach, loss of data, loss of goodwill or reputation or other pecuniary
        loss suffered by you, even if we or any of our Representatives have been
        advised of their possible existence; nor (b) any direct damage loss or
        expenses arising from loss of customers, loss of profits, loss of
        anticipated profits or loss of savings security breach or loss of data.
      </p>
      <p>
        15.4 Without prejudice to Clause 15.3, neither we nor any Representative
        shall be liable to you for any direct or indirect damage, loss or
        expenses suffered by you as a result of: (a) any viruses, worms, “Trojan
        horses” or similar programs, denial of service attack, spamming or
        hacking or consequential damages or any claim against us by any other
        party; or (b) any fault, inaccuracy, omission, delay or any other
        failure in a Site or a Service caused by your computer equipment or
        arising from your use of the Service on such equipment; or (c) any
        inaccuracies or errors in or omissions from any Content including, but
        not limited to, quotes and financial data; or (d) any delays,
        interruptions or short-term or immaterial errors in the transmission or
        delivery of any Site or a Service; or (e) any additions, deletions or
        changes made to the Content at any time.
      </p>
      <p>
        15.5 Without prejudice to Clauses 15.3 and 15.4 and except where you are
        a Subscriber who has paid the relevant subscription charges, neither we
        nor any Representative shall be liable to you for any direct or indirect
        loss, damage or expenses arising by any reason of non-performance by us
        of these Terms or any other term of our agreement with you.
      </p>
      <p>
        15.6 Without prejudice to Clause 25 below, to the extent that the
        provisions of any applicable law shall impose restrictions on the extent
        to which liability can be excluded under these Terms including, for the
        avoidance of doubt, the provisions of sections 3, 6 and 11 of the Unfair
        Contract Terms Act 1977 in the UK (and its equivalent in any other
        jurisdiction) relating to the requirement of reasonableness, the
        exclusions set out in this Clause 15 shall be limited in accordance with
        such restrictions. However, any exclusions of liability that are not
        affected by such restrictions shall remain in full force and effect.
      </p>
      <p>
        15.7 Without prejudice to the limitation of liability provisions above,
        in the event that you incur any loss, damage or expense arising out of
        your use of any Site, Content or a Service, you agree that our liability
        to you shall be limited to the sum of the subscription fees payable by
        you for Services provided in the twelve (12) month period preceding the
        date on which the claim arose (which, for the avoidance of doubt, shall
        not include (a) any fees paid during such period for Services provided
        before such period, nor (b) any fees paid in advance during such period
        for Services provided after such period).
      </p>
      <p><strong>16 INDEMNITY</strong></p>
      <p>
        You agree to indemnify and hold us, our Representatives, licensors and
        sub-contractors harmless against all costs, claims, damages, liability
        and expenses (including any professional fees) which we might incur by
        reason of a breach by you of these Terms including, without limitation,
        any of the warranties, representations and undertakings in Clauses 4 and
        10 above. For the avoidance of doubt, this indemnity shall extend
        (without limitation) to any losses which we may suffer as a result of
        the use by third parties of your user name and password, except in
        circumstances where you can demonstrate to our satisfaction that such
        third party obtained the relevant details through no fault (including,
        without limitation, negligence) of your own.
      </p>
      <p><strong>17 CHANGES TO THESE TERMS</strong></p>
      <p>
        We may make amendments to the provisions of these Terms from time to
        time. Any amended Terms will be notified to you by being posted on the
        Sites and material changes will be emailed to our Registered Users and
        Subscribers. It is your responsibility to refer regularly to these Terms
        and note any amendments. Amendments will come into effect immediately on
        the amended Terms being posted on the Sites and you will be deemed to
        have accepted them if you access a Site after that time. If you do not
        wish to accept them, you must cancel these Terms as described above.
      </p>
      <p><strong>18 COMPETITIONS AND PRIZES</strong></p>
      <p>
        From time to time we may run competitions, free prize draws and
        promotions on the Sites. These are subject to additional terms that will
        be made available at the time of such competitions, and such additional
        terms shall be deemed incorporated into these Terms.
      </p>
      <p><strong>19 CHANGES TO CONTENT</strong></p>
      <p>
        We reserve the right to delete or change any aspect of the Content, the
        Interactive Areas, the Site and/or any of the technical specifications
        or any aspect of the same. You agree and acknowledge that such changes
        may result in you being unable to access the whole or any part of a
        Site.
      </p>
      <p><strong>20 NO WAIVER</strong></p>
      <p>
        Failure by either party to exercise any right or remedy under these
        Terms does not constitute a waiver of that right or remedy.
      </p>
      <p><strong>21 ENTIRE AGREEMENT</strong></p>
      <p>
        These Terms (including any terms incorporated by reference in these
        Terms), constitute the entire agreement between you and us with respect
        to your access to and use of any Site or receipt of any Service and
        supersede all prior agreements, negotiations and discussions between you
        and us relating to the same. Without prejudice to Clause 15.2 above, we
        exclude any representations and warranties previously given or made in
        relation to the same.
      </p>
      <p><strong>22 LAW AND JURISDICTION</strong></p>
      <p>
        22.1 Where you visit, register and/or subscribe to a Benchmark Site or
        related Service (as indicated on the Site or otherwise notified to you),
        these Terms (and any dispute or claim arising out of or in connection
        with these terms, including non-contractual disputes or claims), to the
        maximum extent permissible under the law of the territory that you are
        located in, will be governed by the laws of England and Wales and will
        be subject to the non-exclusive jurisdiction of the English courts.
      </p>
      <p><strong>23 FORCE MAJEURE</strong></p>
      <p>
        23.1 We shall not be deemed to be in breach of these Terms by reason of
        any delay in performing, or any failure to perform any service or our
        obligations in relation to these Terms, if the delay or failure was due
        to any cause beyond our reasonable control, including but not limited to
        acts of God, explosions, floods, fire or accident, war or threat of war,
        terrorism or threat of terrorism, sabotage, civil disturbance,
        epidemics, prohibitions or measures of any kind on the part of any
        governmental, parliamentary or local authority, import or export
        regulations or embargoes, or industrial actions or trade disputes
        (whether involving our employees or of third parties).
      </p>
      <p>
        23.2 In the event we are so hindered or prevented, we shall take
        reasonable steps to give notice of suspension as soon as reasonably
        possible to Subscribers and Registered Users, stating the date and
        extent of the suspension and its cause. We shall resume the performance
        of those obligations that have been suspended as soon as reasonably
        possible after the removal of the cause. In the event that the cause
        continues for more than one (1) month we shall refund you for any
        subscription charges you have paid in advance for any suspended Service.
      </p>
      <p><strong>24 ASSIGNMENT AND SUB-CONTRACTING</strong></p>
      <p>
        24.1 You may not assign, sub-license or otherwise transfer any of your
        rights under these Terms without our prior written consent.
      </p>
      <p>
        24.2 We may assign, sub-contract or transfer any and all of our rights
        and obligations under these Terms to any entity who acquires a
        substantial part of the assets of our business without your prior
        written consent.
      </p>
      <p><strong>25 SEVERABILITY</strong></p>
      <p>
        If any provision of these Terms is found to be wholly or partially
        invalid, void or unenforceable by any court having competent
        jurisdiction or by virtue of any legislation or any other reason, that
        provision shall be invalid, void or unenforceable to that extent only
        and no further and the validity and enforceability of the remaining
        provisions of these Terms shall not be affected.
      </p>
      <p><strong>26 NOTICES</strong></p>
      <p>
        Any notice which is required to be given pursuant to these Terms shall
        be made by email or first class post, in the case of you, to the address
        provided on your registration form and, in the case of us, to the
        address posted on the relevant Site or otherwise notified to you in
        relation to any relevant Service. Any such notice shall be deemed to
        have arrived if sent by post within three (3) days of posting and if
        sent by email at the time of transmission.
      </p>
      <p><strong>27 THIRD PARTY RIGHTS</strong></p>
      <p>
        No term of these Terms is intended to be enforceable by any person other
        than you or us.
      </p>
      <p><strong>28 HEADINGS</strong></p>
      <p>
        Headings in these Terms are for convenience only and will have no legal
        meaning or effect. Benchmark Mineral Intelligence Limited is a company
        registered in England and Wales under number 8922443 whose registered
        office is at Suite 51 Pure Offices, Kembrey Park, Swindon, England, SN2
        8BW and VAT number is GB195254583
      </p>
    </div>
  </SimplePageContent>
</template>
